/**
 * Utility class for handling age range selection.
 * @param selector The selector for the age range selector container.
 * @param options The options for the age range selector.
 * 
 * @example
 * import AgeRangeSelector from "./ageRangeSelector";
 * window.WFAgeRangeSelector = new AgeRangeSelector(".age-range-selector", {});
 * window.WFAgeRangeSelector.init();
 *
 * window.WFAgeRangeSelector.addSelectListener((age: string, minAge: string, maxAge: string) => {
 *   console.log(age, minAge, maxAge);
 * });
 */
type AgeSelectCallback = (age: string, minAge: string, maxAge: string, label: string) => void;

interface AgeRangeOption {
    text: string;
    minAge: number;
    maxAge: number;
    children: AgeRangeOption[];
}

export default class AgeRangeSelector {
    private selectCallbacks: AgeSelectCallback[] = [];
    private boundTriggers: WeakSet<HTMLElement> = new WeakSet();

    constructor(selector: string, options: any) {
        const defaults = {
            selectors: {
                hasChildren: '.has-children',
                hasChildrenLink: '.has-children > a',
                ageLink: '[data-age]'
            },
            closeOnOutsideClick: true,
            availableAgeRanges: []
        };

        if(typeof selector === 'string') {
            this.container = document.querySelector(selector);    
        }
        else if(typeof selector === 'object') {
            this.container = selector;
        }
        
        this.options = Object.assign({}, defaults, options);
    }

    init() {
        this.buildPickerContent();
        this.bindTriggers();
        this.bindDocumentClick();
    }
    
    remove() {
        this.hidePicker();
    }
    
    private buildPickerContent() {
        if (!this.container) return;

        const picker = document.createElement('div');
        picker.style.position = 'absolute';
        picker.style.top = '-1000px';
        picker.style.left = '-1000px';
        picker.style.zIndex = '10000';

        const ageRanges: AgeRangeOption[] = this.options.availableAgeRanges ?? [];
        picker.appendChild(this.renderAgeRangeDropdown(ageRanges));

        this.container.appendChild(picker);
        
        this.picker = picker;
    }

    private bindDocumentClick() {
        document.addEventListener('click', (e: MouseEvent) => {
            const isVisible = this.container.style.top !== '-1000px' && this.container.style.left !== '-1000px';
            if (!isVisible) return;

            const target = e.target as HTMLElement;
            if (this.container.contains(target)) return;
            if (target.closest(this.options.selectors.trigger)) return;

            this.hidePicker();
        });
    }
    
    bindTriggers() {
        
    }

    onShowPicker(e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
        this.pickerOverflow = false;
        const trigger = e.currentTarget as HTMLElement;
        const y = (trigger.offsetTop + trigger.offsetHeight);
        let x = trigger.offsetLeft;
        
        const pickerRect = this.picker.getBoundingClientRect();
        var pickerRightPosition = x + pickerRect.width;
        if(pickerRightPosition > window.innerWidth) {
            x = window.innerWidth - pickerRect.width;
            this.pickerOverflow = true;
        }

        this.picker.style.left = `${x}px`;
        this.picker.style.top = `${y}px`;
        
    }

    openChild(childSelector: string) {
        const child = this.container.querySelector(`.${childSelector}`) as HTMLElement;
        if (!child) return;

        child.classList.remove('hidden');
        child.classList.add('flex');
    }

    closeAllChildren() {
        const openChildren = this.container.querySelectorAll('[class*="age-range-children-"]:not(.hidden)');
        openChildren.forEach((child: HTMLElement) => {
            child.classList.add('hidden');
            child.classList.remove('flex');
        });
    }
    
    onAgeSelect(e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();

        const link = e.currentTarget as HTMLElement;
        const age = link.dataset.age;
        const minAge = link.dataset.minAge;
        const maxAge = link.dataset.maxAge;
        const label = link.textContent?.trim() ?? age;

        this.hidePicker();
        this.selectCallbacks.forEach(cb => cb(age, minAge, maxAge, label, e));
    }

    hidePicker() {
        this.picker.style.top = '-1000px';
        this.picker.style.left = '-1000px';
    }

    addSelectListener(callback: AgeSelectCallback) {
        this.selectCallbacks.push(callback);
    }

    getAgeRangeFromAge(age: number): { age: string; minAge: string; maxAge: string; label: string } | null {
        const ageLinks = this.container.querySelectorAll<HTMLElement>(this.options.selectors.ageLink);
        for (const link of ageLinks) {
            const minAge = parseInt(link.dataset.minAge ?? '0');
            const maxAge = parseInt(link.dataset.maxAge ?? '0');
            if ((minAge === 0 || age >= minAge) && (maxAge === 0 || age <= maxAge)) {
                return {
                    age: link.dataset.age,
                    minAge: link.dataset.minAge,
                    maxAge: link.dataset.maxAge,
                    label: link.textContent?.trim() ?? link.dataset.age,
                };
            }
        }
        return null;
    }

    renderAgeRangeDropdown(options: any[]) {
        var dropdown = document.createElement('div');
        dropdown.classList.add('age-range-selector-dropdown');
        dropdown.classList.add('whitespace-nowrap');
        dropdown.classList.add('rounded-md');
        dropdown.classList.add('border');
        dropdown.classList.add('border-grey');
        dropdown.classList.add('bg-white');
        dropdown.classList.add('text-base');

        const renderOption = (options: any[]) => {
            const items = [];
            for(const option of options) {
                if(option.children.length > 0) {
                    const optionElement = document.createElement('div');
                    optionElement.classList.add('relative', 'group');

                    const mainOption = document.createElement('div');
                    mainOption.classList.add('dropdown-option', 'p-3', 'cursor-pointer', 'hover:bg-coolGrey', 'flex', 'items-center', 'justify-between');

                    const mainOptionText = document.createElement('span');
                    mainOptionText.textContent = option.text;

                    const svgIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                    svgIcon.classList.add('text-gray-800');
                    svgIcon.setAttribute('fill', 'none');
                    svgIcon.setAttribute('viewBox', '0 0 24 24');
                    svgIcon.setAttribute('stroke', 'currentColor');
                    svgIcon.setAttribute('width', '20');
                    svgIcon.setAttribute('height', '20');

                    const svgPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                    svgPath.setAttribute('stroke-linecap', 'round');
                    svgPath.setAttribute('stroke-linejoin', 'round');
                    svgPath.setAttribute('stroke-width', '2');
                    svgPath.setAttribute('d', 'M9 5l7 7-7 7');

                    svgIcon.appendChild(svgPath);
                    mainOption.appendChild(mainOptionText);
                    mainOption.appendChild(svgIcon);

                    const submenuPanel = document.createElement('div');
                    submenuPanel.classList.add(
                        'absolute', 'left-full', 'top-0', '-ml-2', 'pl-2',
                        'opacity-0', 'pointer-events-none', 'transition-opacity', 'duration-100',
                        'group-hover:opacity-100', 'group-hover:pointer-events-auto'
                    );

                    optionElement.addEventListener('mouseenter', () => {
                        let wouldOverflow = false;
                        const rect = submenuPanel.getBoundingClientRect();
                        var pickerRect = this.picker.getBoundingClientRect();
                        const pickerRightEdge = pickerRect.x + pickerRect.width;
                        if(pickerRightEdge + rect.width > window.innerWidth) {
                            wouldOverflow = true;
                        }
                        
                        submenuPanel.classList.toggle('left-full', !wouldOverflow);
                        submenuPanel.classList.toggle('right-full', wouldOverflow);
                    });

                    const childWrapper = document.createElement('div');
                    childWrapper.classList.add('rounded-md', 'border', 'border-grey', 'bg-white', 'text-base');

                    const children = renderOption(option.children);
                    if(children.length > 0) {
                        for(const child of children) {
                            childWrapper.appendChild(child);
                        }
                    }

                    submenuPanel.appendChild(childWrapper);

                    optionElement.appendChild(mainOption);
                    optionElement.appendChild(submenuPanel);

                    items.push(optionElement);
                }
                else {
                    const ageToUse = option.minAge || option.maxAge;    
                    
                    const optionElement = document.createElement('div');
                    optionElement.classList.add('whitespace-nowrap');
                    optionElement.classList.add('dropdown-option');
                    optionElement.classList.add('p-3');
                    optionElement.classList.add('cursor-pointer');
                    optionElement.classList.add('hover:bg-coolGrey');
                    optionElement.textContent = option.text;
                    optionElement.dataset.age = ageToUse;
                    optionElement.dataset.minAge = option.minAge;
                    optionElement.dataset.maxAge = option.maxAge;
                    
                    optionElement.addEventListener('click', this.onAgeSelect.bind(this));

                    items.push(optionElement);
                }
            }

            return items;
        }

        if(options.length > 0) {
            var items = renderOption(options);
            if(items.length > 0) {
                for(const item of items) {
                    dropdown.appendChild(item);
                }
            }
        }
        
        return dropdown;
    }
}